import { useMatomo } from '@datapunt/matomo-tracker-react';
import React, { memo, useEffect, useState } from 'react';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';

import { useAppActions } from '@store/useAppStore';
import { selectAppPersistDarkMode, useAppPersistActions } from '@store/useAppStorePersist';

import { geographyStyle } from '../../constants/mapConfig';
import { getMarkersList } from '../../helpers/getMarkersList';
import { selectMapData, selectMapGeoState, useMapActions } from '../../store/useMapStore';
import MapMarkerSvg from '../MapMarkerSvg/MapMarkerSvg';
import style from './style.module.scss';

const SimpleMaps = ({ tooltipId }) => {
  const [geoUrlState, setGeoUrlState] = useState();

  const { trackEvent } = useMatomo();
  const { setAppPersistMapsVisible } = useAppPersistActions();
  const { setMapGeoState, setMapTooltipContentStatic } = useMapActions();
  const { setAppLocationSelectValue } = useAppActions();

  const { geoUrl, center, scale, geoUnit } = selectMapGeoState();
  const { allowedAreas, facility } = selectMapData();
  const darkMode = selectAppPersistDarkMode();

  const markersList = getMarkersList(facility, scale, geoUnit);
  const isRegion = geoUnit.type == 'region' && geoUnit.name;
  const mapStyles = geographyStyle(darkMode);

  const handleGeographyClick = ({ type, name }) => {
    if (isRegion || !allowedAreas[name]) return;

    trackEvent({ category: 'Map Usage', action: 'map-interaction ' });
    setMapGeoState({ type, name });
  };

  const handleLinkClick = (region, country, name, facility_info) => {
    trackEvent({ category: 'Map Usage', action: 'map-interaction ' });
    setMapGeoState({ type: 'country', name: country, facilityName: name });
    setAppPersistMapsVisible(false);
    setAppLocationSelectValue(facility_info);
  };

  useEffect(() => {
    setGeoUrlState(geoUrl);
  }, [geoUrl]);

  return (
    <ComposableMap
      className={style.simpleMaps}
      projection="geoMercator"
      projectionConfig={{
        rotate: [-12, 0],
        center: center,
        scale: scale,
      }}
    >
      <Geographies geography={geoUrlState}>
        {({ geographies }) =>
          (isRegion ? [geographies.find((geo) => geo.properties.name == geoUnit.name)] : geographies).map(
            (geo, idx) =>
              geo && (
                <Geography
                  key={geo.properties.name + idx}
                  geography={geo}
                  style={mapStyles}
                  onClick={() => {
                    setMapTooltipContentStatic(null);
                    handleGeographyClick(geo.properties);
                  }}
                  data-tooltip-id={tooltipId}
                  data-tooltip-content={geo.properties.name}
                />
              ),
          )
        }
      </Geographies>
      {markersList.map((marker) =>
        Array.isArray(marker) ? (
          <Marker
            className={style.marker}
            key={marker[0].facility_id}
            coordinates={[marker[0].lng, marker[0].lat]}
            data-tooltip-id={tooltipId}
            data-tooltip-content={marker.map(({ name }) => name).join(', ')}
            onClick={() => setMapTooltipContentStatic(marker)}
          >
            <circle className={style.circle} r={9} />
            <text className={style.text} textAnchor="middle" y={4}>
              {marker.length}
            </text>
          </Marker>
        ) : (
          <Marker
            className={style.marker}
            key={marker.facility_id}
            coordinates={[marker.lng, marker.lat]}
            data-tooltip-id={tooltipId}
            data-tooltip-content={marker.name}
          >
            <MapMarkerSvg
              type="mapMarker"
              variant={marker.mapMarker?.icon}
              color={marker.mapMarker?.color}
              onClick={() =>
                handleLinkClick(marker.region, marker.country, marker.name, marker.facility_info)
              }
            />
          </Marker>
        ),
      )}
    </ComposableMap>
  );
};

export default memo(SimpleMaps);
